export type AppColorThemeId = 'default' | 'demo' | 'high-contrast'

export interface AppColorThemeOption {
  id: AppColorThemeId
  label: string
}

export const appColorThemeOptions: AppColorThemeOption[] = [
  { id: 'default', label: 'Default' },
  { id: 'demo', label: 'Demo' },
  { id: 'high-contrast', label: 'High contrast' },
]

const cookieName = 'app-color-theme'

const allowedThemes = new Set<AppColorThemeId>(['default', 'demo', 'high-contrast'])

function normalizeTheme(value: string | null | undefined): AppColorThemeId {
  if (value && allowedThemes.has(value as AppColorThemeId)) {
    return value as AppColorThemeId
  }
  return 'default'
}

export function useAppColorTheme() {
  const appColorTheme = useCookie<AppColorThemeId>(cookieName, {
    default: () => 'default',
    sameSite: 'lax',
    maxAge: 60 * 60 * 24 * 365,
  })

  watch(
    appColorTheme,
    (v) => {
      const n = normalizeTheme(v)
      if (n !== v) {
        appColorTheme.value = n
      }
    },
    { immediate: true },
  )

  function setAppColorTheme(id: AppColorThemeId) {
    appColorTheme.value = id
  }

  return {
    appColorTheme,
    setAppColorTheme,
    options: appColorThemeOptions,
  }
}

/** Value for `<html data-app-theme>` (must match CSS selectors in `assets/css/main.css`). */
export function appColorThemeHtmlAttr(value: string | null | undefined): AppColorThemeId {
  return normalizeTheme(value)
}
